import React, { FC, useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckTable,
  AckTableRowFormatter,
  AckTableRowProps,
  convertToDisplayValue,
  formatCurrency,
  ModelSpec,
  useFetch,
} from "ackbox-weblib-react";
import { Accordion, Empty, formatDecoratedCurrency, SUMMARIES_API_PATH, useEffectOnce } from "./Constants";
import { Progress, useProgress } from "./Progress";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const TransactionsBreakdownSummary = () => {
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const [data, setData] = useState([] as any[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const Details: FC<{ title: string, transactions: any[] }> = (detailsProps) => {
    const spec: ModelSpec[] = [
      { key: "category", label: "Category", name: "category", type: "string" },
      { key: "total", label: "Total", name: "total", type: "currency" },
      { key: "per-month", label: "Per Month", name: "per-month", type: "currency" },
    ];
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "per-month") {
        return formatCurrency(payload["total"] / 12);
      }
      return convertToDisplayValue(spec, payload);
    };
    const rows = detailsProps.transactions.map((value: any) => ({ spec: spec, payload: value }));
    return (
      <>
        {rows.length === 0 && <Empty/>}
        {rows.length !== 0 && <Accordion title={detailsProps.title}>
            <AckTable rows={rows} formatters={formatters}/>
        </Accordion>}
      </>
    );
  };

  const { errors, isLoading } = useEffectOnce(async () => {
    const values = await ackFetch.get("yearly-transactions/");
    const spec: ModelSpec[] = [
      { key: "id", label: "Year", name: "id", type: "string" },
      { key: "incomes", label: "Incomes", name: "incomes", type: "currency" },
      { key: "expenses", label: "Expenses", name: "expenses", type: "currency" },
      { key: "total", label: "Total", name: "total", type: "currency" },
    ];
    const rows: AckTableRowProps[] = values.map((summary: any) => ({ spec: spec, payload: summary }));

    setRows(rows);
    setData(values);
  });

  const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
    if (spec.name === "incomes") {
      return (<Details title={formatCurrency(payload[spec.name])} transactions={payload["incomesBreakdown"]}/>);
    }
    if (spec.name === "expenses") {
      return (<Details title={formatCurrency(payload[spec.name])} transactions={payload["expensesBreakdown"]}/>);
    }
    if (spec.name === "total") {
      return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
    }
    return convertToDisplayValue(spec, payload);
  };

  const Chart = () => {
    const formatter = (value: any) => formatCurrency(value);
    return (
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 2"/>
            <XAxis dataKey="id" angle={-45} height={60} interval={1} tickMargin={20} tick={{ fontSize: 10 }}/>
            <YAxis tickCount={6} interval={0} tick={{ fontSize: 12 }} tickFormatter={formatter}/>
            <Tooltip formatter={formatter}/>
            <Legend layout="horizontal"/>
            <Bar dataKey="incomes" opacity="0.75" stroke="#f1c40f" fill="#f1c40f"/>
            <Bar dataKey="expenses" opacity="0.75" stroke="#d35400" fill="#d35400"/>
            <Bar dataKey="total" opacity="0.75" stroke="#1abc9c" fill="#1abc9c"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>

      <h1 className="subtitle">History</h1>
      {!isLoading && data.length === 0 && <Empty/>}
      {!isLoading && data.length > 0 && <Chart/>}

      <h1 className="subtitle">Transactions Breakdown</h1>
      {!isLoading && rows.length === 0 && <Empty/>}
      {!isLoading && rows.length > 0 && <AckTable rows={rows} formatters={formatters} hideIndexCol={true}/>}
    </>
  );
};

export const YearlySummary = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Yearly Summary</h1>
      {!progress.isComplete && <Progress {...progress}/>}
      {progress.isComplete && <TransactionsBreakdownSummary/>}
    </div>
  );
};
